import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { featuresKeys } from "@/application/query-keys/features.keys";
import {
  featuresRepository,
  normalizeFeatureRow,
  type FeatureFilters,
} from "@/infrastructure/repositories/features.supabase.repository";
import type { FeatureRow } from "@/infrastructure/supabase/database.types";

export function useFeaturesList(filters: FeatureFilters = {}) {
  return useQuery({
    queryKey: featuresKeys.list(filters),
    queryFn: () => featuresRepository.list(filters),
  });
}

export function useFeatureDetail(id: string | undefined) {
  return useQuery({
    queryKey: featuresKeys.detail(id ?? ""),
    queryFn: () => featuresRepository.byId(id as string),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

function patchFeatureInListCaches(queryClient: QueryClient, row: FeatureRow) {
  queryClient.setQueriesData<FeatureRow[]>(
    {
      predicate: (q) => {
        const key = q.queryKey;
        return key[0] === "features" && key[1] === "list";
      },
    },
    (old) => {
      if (!old) return old;
      return old.map((f) => (f.id === row.id ? normalizeFeatureRow(row) : f));
    }
  );
}

/** Optimistic merge: detail (if cached) + all feature lists. */
function applyFeaturePatchToCaches(
  queryClient: QueryClient,
  id: string,
  patch: Partial<FeatureRow>
) {
  const detailKey = featuresKeys.detail(id);
  const prevDetail = queryClient.getQueryData<FeatureRow>(detailKey);
  if (prevDetail) {
    queryClient.setQueryData(
      detailKey,
      normalizeFeatureRow({ ...prevDetail, ...patch } as FeatureRow)
    );
  }
  queryClient.setQueriesData<FeatureRow[]>(
    {
      predicate: (q) => {
        const key = q.queryKey;
        return key[0] === "features" && key[1] === "list";
      },
    },
    (old) => {
      if (!old) return old;
      return old.map((f) =>
        f.id === id ? normalizeFeatureRow({ ...f, ...patch } as FeatureRow) : f
      );
    }
  );
}

export function useDeleteFeature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => featuresRepository.delete(id),
    onMutate: (id) => {
      // Optimistically evict from all list caches and the detail cache.
      const prevDetail = queryClient.getQueryData<FeatureRow>(featuresKeys.detail(id));
      queryClient.setQueriesData<FeatureRow[]>(
        {
          predicate: (q) => {
            const key = q.queryKey;
            return key[0] === "features" && key[1] === "list";
          },
        },
        (old) => (old ? old.filter((f) => f.id !== id) : old)
      );
      queryClient.removeQueries({ queryKey: featuresKeys.detail(id) });
      return { prevDetail };
    },
    onError: (err, id, ctx) => {
      // Rollback: restore the detail row; lists will reconcile on invalidate.
      if (ctx?.prevDetail) {
        queryClient.setQueryData(featuresKeys.detail(id), ctx.prevDetail);
      }
      void queryClient.invalidateQueries({ queryKey: featuresKeys.all });
      console.error("[useDeleteFeature]", err);
    },
    onSuccess: () => {
      // Refetch counts-by-component (feature-count badges on the map).
      void queryClient.invalidateQueries({
        queryKey: [...featuresKeys.all, "counts-by-component"],
      });
    },
  });
}

export function useUpdateFeature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<FeatureRow> }) =>
      featuresRepository.update(id, patch),
    onMutate: ({ id, patch }) => {
      const prevDetail = queryClient.getQueryData<FeatureRow>(featuresKeys.detail(id));
      applyFeaturePatchToCaches(queryClient, id, patch);
      return { prevDetail };
    },
    onError: (err, { id }, ctx) => {
      if (ctx?.prevDetail !== undefined) {
        queryClient.setQueryData(featuresKeys.detail(id), ctx.prevDetail);
      } else {
        queryClient.removeQueries({ queryKey: featuresKeys.detail(id) });
      }
      void queryClient.invalidateQueries({ queryKey: featuresKeys.all });
      console.error("[useUpdateFeature]", err);
    },
    onSuccess: (data) => {
      try {
        queryClient.setQueryData(featuresKeys.detail(data.id), data);
        patchFeatureInListCaches(queryClient, data);
      } catch (cacheErr) {
        console.error("[useUpdateFeature] cache update after success", cacheErr);
        void queryClient.invalidateQueries({ queryKey: featuresKeys.all });
      }
    },
  });
}
